(() => {
  if (customElements.get('pe-gift-message')) return;
  const keys = ['giftnote_to', 'giftnote_from', 'giftnote_message', 'giftnote_email', 'giftnote_phone', 'giftnote_method', 'giftnote_time'];
  const normalize = attributes => Object.fromEntries(keys.map(key => [key, typeof attributes?.[key] === 'string' ? attributes[key] : '']));

  class PeGiftMessage extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.drawer = this.closest('pe-cart-drawer');
      this.details = this.querySelector('details');
      this.form = this.querySelector('[data-pe-gift-form]');
      this.fields = this.querySelector('[data-pe-gift-fields]');
      this.status = this.querySelector('[data-pe-gift-status]');
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.form.addEventListener('submit', event => { event.preventDefault(); event.stopPropagation(); this.save(); }, options);
      this.form.addEventListener('input', () => this.onInput(), options);
      this.form.addEventListener('change', () => this.onInput(), options);
      this.querySelector('[data-pe-gift-cancel]').addEventListener('click', () => this.cancel(), options);
      this.querySelector('[data-pe-gift-remove]').addEventListener('click', () => this.save(true), options);
      this.details.addEventListener('toggle', () => {
        if (this.details.open) this.updateSchedule();
      }, options);
      let initial = {};
      try { initial = JSON.parse(this.querySelector('[data-pe-gift-initial]').textContent); } catch {}
      this.readAttributes(initial, true);
      this.setLocked(Boolean(this.drawer?.busy || this.drawer?.checkingOut));
      for (const name of ['giftnote_saved', 'giftnote_removed']) {
        document.addEventListener(name, () => this.refreshAttributes(), options);
      }
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      this.readRevision = (this.readRevision || 0) + 1;
    }

    field(name) { return this.form.elements.namedItem(name); }

    snapshot() {
      return JSON.stringify(keys.map(key => this.field(key)?.value || ''));
    }

    get dirty() { return this.snapshot() !== this.baseline; }
    get exists() { return Boolean(this.saved?.giftnote_from && this.saved?.giftnote_message); }

    readAttributes(attributes, force = false) {
      if (!this.form) return;
      const keepDraft = !force && (this.saving || this.dirty || this.needsReview);
      if (Object.hasOwn(attributes || {}, '_giftnote_theme_removed')) this.savedMarker = attributes._giftnote_theme_removed || '';
      this.saved = normalize(attributes);
      if (!keepDraft) {
        keys.forEach(key => {
          let value = this.saved[key];
          if (key === 'giftnote_method') value = value || 'tracked';
          if (key === 'giftnote_time' && value) {
            const date = new Date(value);
            value = Number.isNaN(date.getTime()) ? '' : new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
          }
          this.field(key).value = value;
        });
        this.clearValidation();
        this.updateSchedule();
        this.baseline = this.snapshot();
      }
      this.updateLabels();
    }

    updateLabels() {
      this.querySelector('[data-pe-gift-title]').textContent = this.exists ? this.dataset.edit : this.dataset.add;
      this.querySelector('[data-pe-gift-badge]').hidden = !this.exists || this.dirty || this.needsReview;
      this.querySelector('[data-pe-gift-remove]').hidden = !this.exists;
      this.querySelector('[data-pe-gift-count]').textContent = String(this.field('giftnote_message').value.length);
    }

    updateSchedule() {
      const method = this.field('giftnote_method').value;
      const scheduled = method === 'scheduled';
      const time = this.field('giftnote_time');
      time.disabled = !scheduled;
      time.required = scheduled;
      const now = new Date(Date.now() + 60000);
      time.min = new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
      this.querySelector('[data-pe-gift-schedule]').hidden = !scheduled;
      this.querySelectorAll('[data-pe-gift-help]').forEach(el => { el.hidden = el.dataset.peGiftHelp !== method; });
      let zone = '';
      try { zone = Intl.DateTimeFormat().resolvedOptions().timeZone; } catch {}
      this.querySelector('[data-pe-gift-timezone]').textContent = [this.dataset.localTime, zone].filter(Boolean).join(' ');
    }

    clearValidation() {
      this.form.querySelectorAll('input, textarea').forEach(input => input.setCustomValidity(''));
    }

    onInput() {
      this.clearValidation();
      this.message();
      this.updateSchedule();
      this.updateLabels();
    }

    message(text = '', error = false) {
      this.status.textContent = text;
      this.status.hidden = !text;
      this.status.classList.toggle('is-error', error);
    }

    setLocked(locked) {
      this.locked = Boolean(locked);
      if (this.fields) this.fields.disabled = this.locked || Boolean(this.saving);
    }

    beforeCheckout() {
      if (this.saving) return false;
      if (!this.dirty && !this.needsReview) return true;
      this.details.open = true;
      this.message(this.needsReview ? this.dataset.uncertain : this.dataset.unsaved, true);
      this.querySelector('[data-pe-gift-save]').focus({ preventScroll: true });
      this.scrollIntoView({ block: 'nearest' });
      return false;
    }

    async cancel() {
      if (this.saving || this.locked) return;
      if (this.needsReview) {
        this.drawer.setBusy(true);
        try {
          const cart = await this.drawer.json(this.drawer.root + 'cart.js', { signal: AbortSignal.timeout(15000) });
          if (!cart.attributes) throw new Error();
          this.saved = normalize(cart.attributes);
          this.syncVendor(cart.attributes);
          this.needsReview = false;
        } catch {
          this.message(this.dataset.uncertain, true);
          return;
        } finally { this.drawer.setBusy(false); }
      }
      this.readAttributes(this.saved, true);
      this.message();
      this.details.open = false;
      this.details.querySelector('summary').focus({ preventScroll: true });
    }

    values() {
      const attributes = Object.fromEntries(keys.map(key => [key, (this.field(key)?.value || '').trim()]));
      attributes.giftnote_phone = attributes.giftnote_phone.replace(/[\s().-]/g, '');
      this.clearValidation();
      for (const key of ['giftnote_to', 'giftnote_from', 'giftnote_message']) {
        if (!attributes[key]) this.field(key).setCustomValidity(this.dataset.requiredError);
      }
      if (attributes.giftnote_message.length > 210) this.field('giftnote_message').setCustomValidity(this.dataset.lengthError);
      if (!attributes.giftnote_email && !attributes.giftnote_phone) this.field('giftnote_email').setCustomValidity(this.dataset.contactError);
      if (attributes.giftnote_phone && !/^\+[1-9]\d{7,14}$/.test(attributes.giftnote_phone)) this.field('giftnote_phone').setCustomValidity(this.dataset.phoneError);
      if (attributes.giftnote_method === 'scheduled') {
        const date = new Date(attributes.giftnote_time);
        if (!attributes.giftnote_time || Number.isNaN(date.getTime()) || date.getTime() <= Date.now()) this.field('giftnote_time').setCustomValidity(this.dataset.dateError);
        else attributes.giftnote_time = date.toISOString();
      } else attributes.giftnote_time = '';
      if (!this.form.reportValidity()) return null;
      return attributes;
    }

    async removalMarker() {
      const cores = Array.from(document.querySelectorAll('giftnote-core'));
      const bounded = async pending => {
        let timer;
        try { return await Promise.race([pending, new Promise((_, reject) => { timer = setTimeout(() => reject(new Error()), 8000); })]); }
        finally { clearTimeout(timer); }
      };
      if (cores.length && !customElements.get('giftnote-core')) await bounded(customElements.whenDefined('giftnote-core'));
      for (const core of cores) {
        if (core.shopify?.checkout_copy === null && typeof core.shopify.GetCartAttributes === 'function') {
          await bounded(core.shopify.GetCartAttributes(() => {}, core.getAttribute('storefront-token')));
        }
        const copy = core.shopify?.checkout_copy;
        if (copy && copy.removed !== true && copy.giftnote_to && copy.giftnote_from) return typeof copy.giftnote_updated_at === 'string' && copy.giftnote_updated_at ? copy.giftnote_updated_at : 'unstamped';
      }
      return this.savedMarker || null;
    }

    syncVendor(attributes) {
      for (const core of document.querySelectorAll('giftnote-core')) {
        try {
          if (!core.shopify || typeof core.cartCallback !== 'function' || typeof core.hydrate !== 'function' || typeof core.form?.clearForm !== 'function' || typeof core.form?.setForm !== 'function') continue;
          core.shopify.cart_attributes = { ...attributes };
          core.order_exists = false;
          core.order_attributes = null;
          core.form.clearForm();
          core.form.setForm({ giftnote_method: core.form.default_order_method || 'tracked' });
          if (core.form.message_count) core.form.message_count.textContent = '0';
          core.remove_button?.classList.add('hidden');
          core.cartCallback();
          core.hydrate();
        } catch (error) { console.warn('[PE gift] Giftnote display refresh unavailable.'); }
      }
    }

    async refreshAttributes() {
      if (this.saving || !this.drawer?.json) return;
      const revision = this.readRevision = (this.readRevision || 0) + 1;
      try {
        const cart = await this.drawer.json(this.drawer.root + 'cart.js');
        if (revision === this.readRevision && this.isConnected && !this.saving) this.readAttributes(cart.attributes || {});
      } catch {}
    }

    async save(remove = false) {
      if (this.saving || this.locked || this.drawer?.busy || this.drawer?.checkingOut || !this.drawer?.json) return;
      const attributes = remove ? Object.fromEntries(keys.map(key => [key, ''])) : this.values();
      if (!attributes) return;
      if (remove) {
        attributes.giftnote_medium = '';
        attributes.giftnote_source = '';
      } else {
        attributes.giftnote_source = 'theme';
        attributes._giftnote_theme_removed = null;
      }
      this.saving = true;
      this.readRevision = (this.readRevision || 0) + 1;
      this.drawer.revision++;
      this.drawer.readController?.abort();
      this.setAttribute('aria-busy', 'true');
      this.querySelector('[data-pe-gift-save-label]').textContent = this.dataset.saving;
      this.message();
      this.drawer.setBusy(true);
      let confirmed = false;
      let sent = false;
      const matches = cart => cart?.attributes && Object.entries(attributes).every(([key, value]) => (cart.attributes[key] || '') === (value || ''));
      const complete = cart => {
        confirmed = true;
        this.needsReview = false;
        this.readAttributes(cart.attributes, true);
        this.syncVendor(cart.attributes);
        this.message(remove ? this.dataset.removed : this.dataset.saved);
        this.details.open = false;
        this.dispatchEvent(new CustomEvent('pe:gift:saved', { bubbles: true, detail: { removed: remove } }));
      };
      try {
        if (remove) attributes._giftnote_theme_removed = await this.removalMarker();
        sent = true;
        const cart = await this.drawer.json(this.drawer.root + 'cart/update.js', {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ attributes }), signal: AbortSignal.timeout(15000)
        });
        if (!matches(cart)) throw new Error(this.dataset.uncertain);
        complete(cart);
      } catch (error) {
        if (sent && !error.rejected) {
          this.needsReview = true;
          try {
            const cart = await this.drawer.json(this.drawer.root + 'cart.js', { signal: AbortSignal.timeout(15000) });
            if (matches(cart)) complete(cart);
            else if (cart.attributes) this.saved = normalize(cart.attributes);
          } catch {}
        }
        if (!confirmed) {
          this.details.open = true;
          this.updateLabels();
          this.message(error.rejected || !sent ? this.dataset.error : this.dataset.uncertain, true);
        }
      } finally {
        this.saving = false;
        this.removeAttribute('aria-busy');
        this.querySelector('[data-pe-gift-save-label]').textContent = this.dataset.save;
        this.drawer.setBusy(false);
        this.setLocked(Boolean(this.drawer.checkingOut));
        if (confirmed) this.details.querySelector('summary').focus({ preventScroll: true });
      }
    }
  }

  customElements.define('pe-gift-message', PeGiftMessage);
})();
